export type OverflowTabKey = string | number;

export interface OverflowTab {
  key: OverflowTabKey;
}

export const getTabMeasurementKey = (key: OverflowTabKey) =>
  `${typeof key}:${String(key)}`;

export const getPinnedVisibleTabs = <T extends OverflowTab>(
  tabs: T[],
  activeTab: OverflowTabKey,
  maxVisibleTabs: number
) => {
  const visibleCount = Math.max(0, Math.min(maxVisibleTabs, tabs.length));

  if (visibleCount === 0) {
    return [];
  }

  if (visibleCount >= tabs.length) {
    return tabs;
  }

  const visibleTabs = tabs.slice(0, visibleCount);
  const activeVisible = visibleTabs.some((tab) => tab.key === activeTab);

  if (activeVisible) {
    return visibleTabs;
  }

  const activeTabConfig = tabs.find((tab) => tab.key === activeTab);

  if (!activeTabConfig) {
    return visibleTabs;
  }

  if (visibleCount === 1) {
    return [activeTabConfig];
  }

  return [...tabs.slice(0, visibleCount - 1), activeTabConfig];
};

export const getHiddenTabs = <T extends OverflowTab>(
  tabs: T[],
  visibleTabs: T[]
) => {
  const visibleKeys = new Set(visibleTabs.map((tab) => tab.key));

  return tabs.filter((tab) => !visibleKeys.has(tab.key));
};

type WidthLookup =
  | Map<string | number, number>
  | Record<string | number, number | undefined>;

const readWidth = (lookup: WidthLookup, key: string | number) => {
  if (lookup instanceof Map) {
    return lookup.get(key);
  }

  return lookup[key];
};

export interface CalculateFittedVisibleTabCountOptions<T extends OverflowTab> {
  tabs: T[];
  activeTab: OverflowTabKey;
  maxVisibleTabs: number;
  minVisibleTabs?: number;
  containerWidth: number;
  horizontalPadding: number;
  itemGap: number;
  tabWidths: WidthLookup;
  moreButtonWidths: WidthLookup;
  estimateTabWidth: (tab: T) => number;
  estimateMoreButtonWidth: (hiddenCount: number) => number;
}

export const calculateFittedVisibleTabCount = <T extends OverflowTab>({
  tabs,
  activeTab,
  maxVisibleTabs,
  minVisibleTabs = 1,
  containerWidth,
  horizontalPadding,
  itemGap,
  tabWidths,
  moreButtonWidths,
  estimateTabWidth,
  estimateMoreButtonWidth
}: CalculateFittedVisibleTabCountOptions<T>) => {
  const preferredCount = Math.max(0, Math.min(maxVisibleTabs, tabs.length));

  if (preferredCount === 0) {
    return 0;
  }

  const minimumCount = Math.max(1, Math.min(minVisibleTabs, preferredCount));
  const availableWidth = containerWidth - horizontalPadding;

  if (!Number.isFinite(availableWidth) || availableWidth <= 0) {
    return preferredCount;
  }

  for (let count = preferredCount; count >= minimumCount; count -= 1) {
    const visibleTabs = getPinnedVisibleTabs(tabs, activeTab, count);
    const hiddenCount = tabs.length - visibleTabs.length;
    const renderedItemCount = visibleTabs.length + (hiddenCount > 0 ? 1 : 0);
    const gapWidth = Math.max(0, renderedItemCount - 1) * itemGap;
    const tabsWidth = visibleTabs.reduce((total, tab) => {
      const measuredWidth = readWidth(tabWidths, getTabMeasurementKey(tab.key));

      return total + (measuredWidth ?? estimateTabWidth(tab));
    }, 0);
    const moreWidth =
      hiddenCount > 0
        ? readWidth(moreButtonWidths, hiddenCount) ??
          estimateMoreButtonWidth(hiddenCount)
        : 0;
    const rowWidth = tabsWidth + moreWidth + gapWidth;

    if (rowWidth <= availableWidth) {
      return count;
    }
  }

  return minimumCount;
};
